/**
 * vehicle-trip.ts
 * add-locx-trip-fuel-tracking PR1 — contracts for servicios.vehicle_trip
 * ("gerenciamiento de viaje" in the legacy BRAVA system).
 *
 * An operator requests authorization to circulate a vehicle (origin/destination
 * + a documentation checklist). A supervisor/admin approves/rejects it, or
 * marks it as not-carried-out, from apps/frontend (the tenant admin panel) —
 * the field operator never approves their own request.
 *
 * Structural clone of contracts-servicios/fleet-failure.ts's
 * fleet_failure_report shape (same FSM-backed-by-identity.state posture).
 *
 * Spec: openspec/changes/add-locx-trip-fuel-tracking/design.md §2/§3
 */
import { z } from 'zod';
import { CapturedAtSchema, ClientRequestIdSchema } from '@suite/contracts-kernel';

// ---------------------------------------------------------------------------
// FSM states (design.md §2)
// ---------------------------------------------------------------------------

export const VEHICLE_TRIP_STATES = ['SOLICITADO', 'APROBADO', 'RECHAZADO', 'NO_REALIZADO'] as const;
export type VehicleTripFsmState = (typeof VEHICLE_TRIP_STATES)[number];

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export const VehicleTripCreateSchema = z.object({
  assetId: z.string().uuid(),
  origin: z.string().min(1, 'origin is required'),
  destination: z.string().min(1, 'destination is required'),
  // Documentation checklist — booleans, default false (mirrors the legacy
  // "chapa patente delantera/trasera, cédula, seguro, VTV" checklist).
  frontPlateOk: z.boolean().default(false),
  rearPlateOk: z.boolean().default(false),
  registrationOk: z.boolean().default(false),
  insuranceOk: z.boolean().default(false),
  inspectionOk: z.boolean().default(false),
  notes: z.string().optional(),
  // operator-offline-forms — idempotency key and phone capture time
  // (becomes requested_at inside a 72 h window). Both optional (S4).
  clientRequestId: ClientRequestIdSchema.optional(),
  capturedAt: CapturedAtSchema.optional(),
  // requestedByEmploymentId NEVER here — resolved server-side via
  // IEmployeeLookup.lookupByUserId(TenantContext.userId) (KC-3.5).
  // currentStateId NEVER here — initial state is always SOLICITADO.
});
export type VehicleTripCreate = z.infer<typeof VehicleTripCreateSchema>;

// ---------------------------------------------------------------------------
// Transition — design.md §2: SOLICITADO is never a transition target (it is
// only the initial state); APROBADO can additionally move to NO_REALIZADO
// ("se aprobó pero el viaje no se concretó").
// ---------------------------------------------------------------------------

export const VehicleTripTransitionSchema = z.object({
  toState: z.enum(['APROBADO', 'RECHAZADO', 'NO_REALIZADO'], {
    errorMap: () => ({ message: 'toState must be one of: APROBADO, RECHAZADO, NO_REALIZADO' }),
  }),
});
export type VehicleTripTransition = z.infer<typeof VehicleTripTransitionSchema>;

// ---------------------------------------------------------------------------
// Response
// ---------------------------------------------------------------------------

export const VehicleTripResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  assetId: z.string().uuid(),
  requestedByEmploymentId: z.string().uuid().nullable(),
  origin: z.string(),
  destination: z.string(),
  frontPlateOk: z.boolean(),
  rearPlateOk: z.boolean(),
  registrationOk: z.boolean(),
  insuranceOk: z.boolean(),
  inspectionOk: z.boolean(),
  currentStateId: z.string().uuid(),
  currentState: z.enum(VEHICLE_TRIP_STATES),
  requestedAt: z.string().datetime().or(z.date()),
  notes: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
});
export type VehicleTripResponse = z.infer<typeof VehicleTripResponseSchema>;

export const VehicleTripDetailResponseSchema = VehicleTripResponseSchema.extend({
  availableTransitions: z.array(VehicleTripTransitionSchema.shape.toState),
});
export type VehicleTripDetailResponse = z.infer<typeof VehicleTripDetailResponseSchema>;
